// src/components/CustomNavbar.js - UPDATED WITH CONTACT INFO API
import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Container, Row, Col } from 'react-bootstrap';
import { useLocation, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { BASE_URL, UPLOAD_BASE_URL } from '../services/api';
import './CustomNavbar.css';

// Import social media icons
import {
  FaPhone,
  FaEnvelope,
  FaMapMarkerAlt,
  FaFacebook,
  FaTwitter,
  FaInstagram,
  FaLinkedin,
  FaYoutube,
  FaWhatsapp,
  FaGlobe,
  FaClock
} from 'react-icons/fa';

const CustomNavbar = () => {
  const [expanded, setExpanded] = useState(false);
  const [menuItems, setMenuItems] = useState([]);
  const [siteHeader, setSiteHeader] = useState(null);
  const [loading, setLoading] = useState(true);
  const [apiError, setApiError] = useState(null);
  const location = useLocation();
  const navigate = useNavigate();
  const [activeDropdown, setActiveDropdown] = useState(null);
  const dropdownTimeoutRef = useRef(null);
  const [isMobile, setIsMobile] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const menuRef = useRef(null);
  const dropdownRef = useRef(null);

  // Contact info from API
  const [contactInfo, setContactInfo] = useState({
    phone: '',
    email: '',
    address: '',
    website: '',
    facebook: '',
    twitter: '',
    instagram: '',
    linkedin: '',
    youtube: '',
    hours: ''
  });

  // Social media links from API
  const [socialLinks, setSocialLinks] = useState([
    { icon: FaFacebook, url: 'https://www.facebook.com/vtechcoders/', color: '#1877F2', label: 'Facebook' },
    { icon: FaTwitter, url: '', color: '#1DA1F2', label: 'Twitter' },
    { icon: FaInstagram, url: '', color: '#E4405F', label: 'Instagram' },
    { icon: FaLinkedin, url: '', color: '#0A66C2', label: 'LinkedIn' },
    { icon: FaYoutube, url: '', color: '#FF0000', label: 'YouTube' },
    { icon: FaWhatsapp, url: 'https://wa.me/919535541365', color: '#25D366', label: 'WhatsApp' }
  ]);

  // Handle responsive breakpoints
  const checkResponsive = useCallback(() => {
    const mobile = window.innerWidth < 768;
    setIsMobile(mobile);
    
    if (mobile) {
      setActiveDropdown(null);
    }
  }, []);

  // Fetch contact info from API
  const fetchContactInfo = async () => {
    try {
      const response = await axios.get(`${BASE_URL}/contact_info.php`);
      if (response.data.success && response.data.data && response.data.data.length > 0) {
        const data = response.data.data[0];
        
        // Update contact info
        setContactInfo({
          phone: data.phone_main || '9535541365',
          phoneAlt: data.phone_alternate || '',
          email: data.email_general || 'crm.jain365@gmail.com',
          address: `${data.address_line1 || ''} ${data.address_line2 || ''}, ${data.city || 'Hubli'}, ${data.state || 'Karnataka'} - ${data.postal_code || '580001'}`.trim(),
          website: data.website_url || 'https://vedapromoters.in/',
          facebook: data.facebook_url || 'https://www.facebook.com/vtechcoders/',
          twitter: data.twitter_url || '',
          instagram: data.instagram_url || '',
          linkedin: data.linkedin_url || '',
          youtube: data.youtube_url || '',
          hours: data.mon_fri_hours || ''
        });

        // Update social links
        const links = [];
        if (data.facebook_url) {
          links.push({ icon: FaFacebook, url: data.facebook_url, color: '#1877F2', label: 'Facebook' });
        }
        if (data.twitter_url) {
          links.push({ icon: FaTwitter, url: data.twitter_url, color: '#1DA1F2', label: 'Twitter' });
        }
        if (data.instagram_url) {
          links.push({ icon: FaInstagram, url: data.instagram_url, color: '#E4405F', label: 'Instagram' });
        }
        if (data.linkedin_url) {
          links.push({ icon: FaLinkedin, url: data.linkedin_url, color: '#0A66C2', label: 'LinkedIn' });
        }
        if (data.youtube_url) {
          links.push({ icon: FaYoutube, url: data.youtube_url, color: '#FF0000', label: 'YouTube' });
        }
        // Always add WhatsApp
        links.push({ 
          icon: FaWhatsapp, 
          url: `https://wa.me/${data.phone_main?.replace(/\D/g, '') || '919535541365'}`, 
          color: '#25D366', 
          label: 'WhatsApp' 
        });
        
        setSocialLinks(links);
      }
    } catch (error) {
      console.error('Error fetching contact info:', error);
      // Keep default values
    }
  };

  // Fetch site header data
  const fetchSiteHeader = async () => {
    try {
      const response = await axios.get(`${BASE_URL}/site_header.php`);
      if (response.data.success) {
        const data = response.data.data;
        setSiteHeader(data);
      } else {
        setSiteHeader({
          college_name: 'VTECH CODERS DHARWAD',
          organization_name: 'VTECH CODERS',
          tagline: 'We Code Your IDEAS.',
          logo_image: '',
          logo_icon: 'fas fa-school'
        });
      }
    } catch (error) {
      setSiteHeader({
        college_name: 'VTECH CODERS DHARWAD',
        organization_name: 'VTECH CODERS',
        tagline: 'We Code Your IDEAS.',
        logo_image: '',
        logo_icon: 'fas fa-school'
      });
    }
  };

  // Fetch menu items
  const fetchMenuItems = async () => {
    try {
      const response = await axios.get(`${BASE_URL}/menu.php`);
      
      if (response.data && response.data.success && response.data.data) {
        const menuData = response.data.data
          .filter(item => item.is_active === 1 || item.is_active === "1")
          .map(item => ({
            ...item,
            id: item.id.toString(),
            hasSubmenu: (item.subMenu && item.subMenu.length > 0) || item.has_submenu === 1,
            subMenu: (item.subMenu || []).filter(sub => 
              sub.is_active === 1 || sub.is_active === "1"
            )
          }))
          .sort((a, b) => (a.menu_order || 0) - (b.menu_order || 0));
        
        setMenuItems(menuData);
      } else {
        setMenuItems([]);
      }
    } catch (error) {
      setMenuItems([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchMenuItems();
    fetchSiteHeader();
    fetchContactInfo();
    checkResponsive();

    const handleScroll = () => {
      setScrolled(window.scrollY > 10);
    };

    const handleResize = () => {
      checkResponsive();
      if (window.innerWidth >= 768) {
        setExpanded(false);
        setActiveDropdown(null);
      }
    };

    // Close dropdowns when clicking outside
    const handleClickOutside = (event) => {
      if (menuRef.current && !menuRef.current.contains(event.target)) {
        setActiveDropdown(null);
      }
    };

    // Close dropdowns when pressing Escape key
    const handleEscapeKey = (event) => {
      if (event.key === 'Escape') {
        setActiveDropdown(null);
      }
    };

    window.addEventListener('scroll', handleScroll);
    window.addEventListener('resize', handleResize);
    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('keydown', handleEscapeKey);

    return () => {
      window.removeEventListener('scroll', handleScroll);
      window.removeEventListener('resize', handleResize);
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleEscapeKey);
      
      if (dropdownTimeoutRef.current) {
        clearTimeout(dropdownTimeoutRef.current);
      }
    };
  }, [checkResponsive]);

  const isActive = (path) => {
    if (path === '/') return location.pathname === '/';
    return location.pathname === path || location.pathname.startsWith(path + '/');
  };

  const handleNavigation = (path, isExternal = false, e) => {
    if (e) e.preventDefault();
    
    if (isMobile) {
      setExpanded(false);
      setActiveDropdown(null);
    }
    
    if (isExternal === true || isExternal === "1" || isExternal === 1) {
      window.open(path, '_blank', 'noopener,noreferrer');
    } else {
      navigate(path);
    }
  };

  // ===== SMOOTH DESKTOP HOVER HANDLERS =====
  const handleMouseEnter = useCallback((itemId) => {
    if (!isMobile) {
      if (dropdownTimeoutRef.current) {
        clearTimeout(dropdownTimeoutRef.current);
        dropdownTimeoutRef.current = null;
      }
      setActiveDropdown(itemId);
    }
  }, [isMobile]);

  const handleMouseLeave = useCallback(() => {
    if (!isMobile) {
      dropdownTimeoutRef.current = setTimeout(() => {
        setActiveDropdown(null);
      }, 150);
    }
  }, [isMobile]);

  const handleDropdownMouseEnter = useCallback(() => {
    if (!isMobile && dropdownTimeoutRef.current) {
      clearTimeout(dropdownTimeoutRef.current);
      dropdownTimeoutRef.current = null;
    }
  }, [isMobile]);

  const handleDropdownMouseLeave = useCallback(() => {
    if (!isMobile) {
      dropdownTimeoutRef.current = setTimeout(() => {
        setActiveDropdown(null);
      }, 100);
    }
  }, [isMobile]);

  const toggleMobileMenu = () => {
    setExpanded(!expanded);
    if (!expanded) {
      setActiveDropdown(null);
    }
  };

  const toggleMobileSubmenu = (itemId) => {
    setActiveDropdown(activeDropdown === itemId ? null : itemId);
  };

  // Format phone number for display
  const formatPhoneNumber = (phone) => {
    if (!phone) return '';
    const cleaned = phone.replace(/\D/g, '');
    if (cleaned.length === 10) {
      return `+91 ${cleaned.slice(0, 5)} ${cleaned.slice(5)}`;
    }
    return phone;
  };

  // Render social icon with validation
  const renderSocialIcon = (social, index) => {
    if (!social.url || social.url.trim() === '') {
      return null;
    }

    const Icon = social.icon;
    return (
      <a 
        key={index}
        href={social.url}
        target="_blank"
        rel="noopener noreferrer"
        className="social-icon"
        style={{ '--social-color': social.color }}
        aria-label={social.label}
      >
        <Icon />
      </a>
    );
  };

  // Render mobile menu items
  const renderMobileMenuItem = (item) => {
    const active = isActive(item.path);
    const hasSubmenu = item.hasSubmenu && item.subMenu && item.subMenu.length > 0;
    const isDropdownOpen = activeDropdown === item.id;
    const isExternal = item.is_external === "1" || item.is_external === 1;

    return (
      <div key={item.id} className={`mobile-nav-item ${isDropdownOpen ? 'active' : ''}`}>
        <button
          className={`mobile-nav-link ${active ? 'active' : ''} ${hasSubmenu ? 'has-dropdown' : ''}`}
          onClick={(e) => {
            if (hasSubmenu) {
              e.preventDefault();
              toggleMobileSubmenu(item.id);
            } else {
              handleNavigation(item.path, isExternal, e);
            }
          }}
          aria-expanded={isDropdownOpen}
          aria-haspopup={hasSubmenu}
        >
          <span className="mobile-nav-content">
            {item.icon && <i className={`${item.icon} me-2`} />}
            <span className="mobile-nav-text">{item.label}</span>
          </span>
          {hasSubmenu && (
            <i className={`mobile-dropdown-icon fas fa-chevron-${isDropdownOpen ? 'up' : 'down'}`} />
          )}
          {isExternal && <i className="fas fa-external-link-alt ms-2 small" />}
        </button>

        {hasSubmenu && (
          <div 
            className={`mobile-dropdown-menu ${isDropdownOpen ? 'show' : ''}`}
            ref={isDropdownOpen ? dropdownRef : null}
          >
            {item.subMenu.map((subItem) => {
              const isSubActive = isActive(subItem.path);
              const isSubExternal = subItem.is_external === "1" || subItem.is_external === 1;

              return (
                <button
                  key={subItem.id}
                  className={`mobile-dropdown-item ${isSubActive ? 'active' : ''}`}
                  onClick={(e) => {
                    handleNavigation(subItem.path, isSubExternal, e);
                    setExpanded(false);
                    setActiveDropdown(null);
                  }}
                >
                  {subItem.icon && subItem.icon !== 'fas fa-link' && (
                    <i className={`${subItem.icon} me-2`} />
                  )}
                  <span className="mobile-dropdown-text">{subItem.label}</span>
                  {isSubExternal && <i className="fas fa-external-link-alt ms-2 small" />}
                </button>
              );
            })}
          </div>
        )}
      </div>
    );
  };

  // Render desktop menu items with SMOOTH hover handling
  const renderDesktopMenuItem = (item) => {
    const active = isActive(item.path);
    const hasSubmenu = item.hasSubmenu && item.subMenu && item.subMenu.length > 0;
    const isDropdownOpen = activeDropdown === item.id;
    const isExternal = item.is_external === "1" || item.is_external === 1;

    if (hasSubmenu) {
      return (
        <div
          key={item.id}
          className="desktop-nav-item dropdown"
          onMouseEnter={() => handleMouseEnter(item.id)}
          onMouseLeave={handleMouseLeave}
        >
          <button
            className={`desktop-nav-link dropdown-toggle ${active ? 'active' : ''}`}
            onClick={(e) => {
              if (isMobile) {
                e.preventDefault();
                toggleMobileSubmenu(item.id);
              } else {
                handleNavigation(item.path, isExternal, e);
              }
            }}
            aria-expanded={isDropdownOpen}
            aria-haspopup="true"
          >
            <span className="desktop-nav-content">
              {item.icon && <i className={`${item.icon} me-2`} />}
              <span className="desktop-nav-text">{item.label}</span>
            </span>
            <i className={`desktop-dropdown-icon fas fa-chevron-${isDropdownOpen ? 'up' : 'down'}`} />
            {isExternal && <i className="fas fa-external-link-alt ms-2 small" />}
          </button>

          {hasSubmenu && (
            <div 
              className={`desktop-dropdown-menu ${isDropdownOpen ? 'show' : ''}`}
              onMouseEnter={handleDropdownMouseEnter}
              onMouseLeave={handleDropdownMouseLeave}
              aria-labelledby={`menu-${item.id}`}
              ref={isDropdownOpen ? dropdownRef : null}
              style={{
                transition: 'all 0.3s cubic-bezier(0.25, 0.8, 0.25, 1)'
              }}
            >
              {item.subMenu.map((subItem) => {
                const isSubActive = isActive(subItem.path);
                const isSubExternal = subItem.is_external === "1" || subItem.is_external === 1;

                return (
                  <button
                    key={subItem.id}
                    className={`desktop-dropdown-item ${isSubActive ? 'active' : ''}`}
                    onClick={(e) => {
                      handleNavigation(subItem.path, isSubExternal, e);
                      setActiveDropdown(null);
                    }}
                    onMouseEnter={(e) => e.currentTarget.style.backgroundColor = 'rgba(255, 255, 255, 0.08)'}
                    onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
                  >
                    {subItem.icon && subItem.icon !== 'fas fa-link' && (
                      <i className={`${subItem.icon} me-2`} />
                    )}
                    <span className="desktop-dropdown-text">{subItem.label}</span>
                    {isSubExternal && <i className="fas fa-external-link-alt ms-2 small" />}
                  </button>
                );
              })}
            </div>
          )}
        </div>
      );
    }

    return (
      <div 
        key={item.id} 
        className="desktop-nav-item"
        onMouseEnter={() => {
          if (activeDropdown && !isMobile) {
            setActiveDropdown(null);
          }
        }}
      >
        <button
          className={`desktop-nav-link ${active ? 'active' : ''}`}
          onClick={(e) => handleNavigation(item.path, isExternal, e)}
        >
          {item.icon && <i className={`${item.icon} me-2`} />}
          <span className="desktop-nav-text">{item.label}</span>
          {isExternal && <i className="fas fa-external-link-alt ms-2 small" />}
        </button>
      </div>
    );
  };

  // Render top contact bar component
  const renderTopContactBar = () => (
    <div className="top-contact-bar">
      <Container fluid>
        <div className="top-contact-container">
          {/* Left side - Contact Info */}
          <div className="top-contact-info">
            <div className="contact-item">
              <FaPhone className="contact-icon" />
              <span className="contact-text">{formatPhoneNumber(contactInfo.phone)}</span>
            </div>
            <div className="contact-item">
              <FaEnvelope className="contact-icon" />
              <span className="contact-text">{contactInfo.email}</span>
            </div>
            <div className="contact-item">
              <FaMapMarkerAlt className="contact-icon" />
              <span className="contact-text" title={contactInfo.address}>
                {contactInfo.address.length > 50 ? 
                  contactInfo.address.substring(0, 50) + '...' : 
                  contactInfo.address
                }
              </span>
            </div>
          </div>
          
          {/* Right side - Social Media */}
          <div className="top-social-media">
            <span className="social-label">Connect:</span>
            <div className="social-icons">
              {socialLinks.map((social, index) => renderSocialIcon(social, index))}
              {contactInfo.website && (
                <a 
                  href={contactInfo.website}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="social-icon"
                  style={{ '--social-color': '#4CAF50' }}
                  aria-label="Website"
                >
                  <FaGlobe />
                </a>
              )}
            </div>
          </div>
        </div>
      </Container>
    </div>
  );

  // Header component
  const renderHeader = () => (
    <div className="navbar-header bg-white">
      <Container fluid>
        <Row className="align-items-center">
          <Col xs={3} sm={3} md={2} lg={2} xl={2} className="p-0">
            <div className="logo-container-left">
              {siteHeader?.logo_image ? (
                <img
                  src={`${UPLOAD_BASE_URL}${siteHeader.logo_image}`}
                  alt={siteHeader.college_name || 'School Logo'}
                  className="navbar-logo"
                  loading="lazy"
                  onError={(e) => {
                    e.target.style.display = 'none';
                    e.target.parentElement.innerHTML = `
                      <div class="logo-icon-container">
                        <i class="${siteHeader?.logo_icon || 'fas fa-school'} navbar-logo-icon"></i>
                      </div>
                    `;
                  }}
                />
              ) : (
                <div className="logo-icon-container">
                  <i className={`${siteHeader?.logo_icon || 'fas fa-school'} navbar-logo-icon`} />
                </div>
              )}
            </div>
          </Col>
          
          <Col xs={9} sm={9} md={10} lg={10} xl={10} className="p-0">
            <div className="navbar-brand-content-center">
              {siteHeader?.organization_name && (
                <div className="navbar-organization-wrapper">
                  <p className="navbar-organization" title={siteHeader.organization_name}>
                    {siteHeader.organization_name}
                  </p>
                </div>
              )}
              
              <div className="navbar-college-name-wrapper">
                <h1 className="navbar-college-name" title={siteHeader?.college_name}>
                  {siteHeader?.college_name || 'VTECH CODERS DHARWAD'}
                </h1>
              </div>
              
              {siteHeader?.tagline && (
                <div className="navbar-tagline-wrapper">
                  <p className="navbar-tagline" title={siteHeader.tagline}>
                    {siteHeader.tagline}
                  </p>
                </div>
              )}
            </div>
          </Col>
        </Row>
      </Container>
    </div>
  );

  if (loading) {
    return (
      <>
        {/* Top Contact Bar Skeleton */}
        <div className="top-contact-bar skeleton-loader">
          <Container fluid>
            <div className="top-contact-container">
              <div className="skeleton-contact"></div>
              <div className="skeleton-social"></div>
            </div>
          </Container>
        </div>

        {/* Header Skeleton */}
        <div className="navbar-header bg-white skeleton-loader">
          <Container fluid>
            <Row className="align-items-center">
              <Col xs={3} sm={3} md={2} lg={2} xl={2} className="p-0">
                <div className="logo-container-left">
                  <div className="logo-skeleton"></div>
                </div>
              </Col>
              
              <Col xs={9} sm={9} md={10} lg={10} xl={10} className="p-0">
                <div className="navbar-brand-content-center">
                  <div className="skeleton-text skeleton-organization"></div>
                  <div className="skeleton-text skeleton-collegename"></div>
                  <div className="skeleton-text skeleton-tagline"></div>
                </div>
              </Col>
            </Row>
          </Container>
        </div>
      </>
    );
  }

  return (
    <>
      {/* Top Contact Bar - Desktop Only */}
      {!isMobile && renderTopContactBar()}

      {/* Main Header */}
      {renderHeader()}

      {apiError && (
        <div className="alert alert-warning alert-dismissible fade show m-2" role="alert">
          <strong>API Error:</strong> {apiError}
          <button type="button" className="btn-close" onClick={() => setApiError(null)}></button>
        </div>
      )}

      {/* Mobile Menu Overlay */}
      {isMobile && expanded && (
        <div 
          className="mobile-menu-overlay"
          onClick={() => setExpanded(false)}
        />
      )}

      {/* Mobile Menu */}
      {isMobile && (
        <>
          <nav className={`mobile-menu-container ${expanded ? 'open' : ''}`}>
            <div className="mobile-menu-header">
              <h5>Menu</h5>
              <button
                className="mobile-menu-close"
                onClick={() => setExpanded(false)}
                aria-label="Close menu"
              >
                <i className="fas fa-times" />
              </button>
            </div>
            
            <div className="mobile-menu-items">
              {menuItems.length > 0 ? (
                menuItems.map(renderMobileMenuItem)
              ) : (
                <div className="no-menu-items">
                  <i className="fas fa-bars fa-2x mb-3" />
                  <p>No menu items</p>
                </div>
              )}
            </div>
          </nav>

          {/* Mobile Menu Button */}
          <nav className={`mobile-sticky-nav ${scrolled ? 'scrolled' : ''}`}>
            <Container fluid>
              <div className="d-flex justify-content-center">
                <button
                  className="mobile-menu-toggle"
                  onClick={toggleMobileMenu}
                  aria-expanded={expanded}
                  aria-label="Toggle navigation"
                >
                  <i className={`fas fa-${expanded ? 'times' : 'bars'} me-2`} />
                  <span>MENU</span>
                </button>
              </div>
            </Container>
          </nav>
        </>
      )}

      {/* Desktop/Tablet Navigation */}
      {!isMobile && (
        <nav 
          className={`desktop-navbar ${scrolled ? 'scrolled' : ''}`}
          ref={menuRef}
        >
          <Container fluid className="px-0">
            <div className="desktop-navbar-container">
              {menuItems.length > 0 ? (
                <div className="desktop-navbar-menu">
                  {menuItems.map(renderDesktopMenuItem)}
                </div>
              ) : (
                <div className="no-menu-items-desktop">
                  <i className="fas fa-exclamation-triangle me-2"></i>
                  No menu items available
                </div>
              )}
            </div>
          </Container>
        </nav>
      )}
    </>
  );
};

export default CustomNavbar;